import React, { useEffect, useRef, useState, useCallback } from "react";
import { MarkdownViewer } from "components/assets/utilities/MarkdownViewer";
import { PieChart, extractPieChartData } from "components/assets/utilities/ChartViews/PieChartView";
import { BarChart, extractBarChartData } from "components/assets/utilities/ChartViews/BarChartView";
import { ScatterPlot, extractScatterPlotData } from "components/assets/utilities/ChartViews/ScatterPlotView";
import { LineGraph, extractLineGraphData } from "components/assets/utilities/ChartViews/LineGraphView";
import { Histogram, extractHistogramData } from "components/assets/utilities/ChartViews/HistogramView";
import { BoxPlot, extractBoxPlotData } from "components/assets/utilities/ChartViews/BoxPlotView";
import { Heatmap, extractHeatmapData } from "components/assets/utilities/ChartViews/HeatmapView";
import { DataTableView } from "components/assets/utilities/ChartViews/DataTableView";
import { escapeCsvValue } from "../../../assets/utilities/PageUtils";
import { JobViewModel } from "../ViewModels/JobViewModel";

export function ResponseSectionView(props: {
    status: string;
    statusMessage?: string;
    outputState: any;
    loadedOutput: any;
    isOutputLoadFailed: boolean;
    outputLoadError: string | null;
    darkMode: boolean;
    parentJobAlias?: string;
    defaultPageSize: number;
    jobID: string;
    job: JobViewModel;
    responseBoxRef: React.RefObject<HTMLDivElement>;
    tableContainerRef: React.RefObject<HTMLDivElement>;
    ellipsis: string;
    onAnimationStateChange?: (isAnimating: boolean) => void;
    onAddQueryToggle?: (query?: string, queryName?: string) => void;
}) {
    const { status, statusMessage, outputState, loadedOutput, isOutputLoadFailed, outputLoadError, darkMode, parentJobAlias, defaultPageSize, jobID, job, responseBoxRef, tableContainerRef, ellipsis, onAnimationStateChange, onAddQueryToggle } = props;
    const [isAnimating, setIsAnimating] = useState(false);

    // Callback to get SQL query for a given data index (1-12)
    const getQueryForDataIndex = useCallback(async (dataIndex: number): Promise<string | null> => {
        try {
            const parameters = await job.page.jobService.getJobParameters(jobID);
            const sqlKey = `SQL ${dataIndex}`;
            const sqlQuery = parameters[sqlKey];
            return sqlQuery ? String(sqlQuery).trim() : null;
        } catch (error) {
            console.error("Failed to get SQL query:", error);
            return null;
        }
    }, [jobID, job]);

    // Handler for add query button
    const handleAddQuery = useCallback(async (dataIndex: number) => {
        // Open the modal immediately with empty values
        if (onAddQueryToggle) {
            onAddQueryToggle(undefined, undefined);
        }
        // Then fetch and populate the query asynchronously
        const query = await getQueryForDataIndex(dataIndex);
        if (query && onAddQueryToggle) {
            // Only update the query if modal is still open (don't toggle)
            // We need to check if modal is open, but since we can't access that state,
            // we'll just update the values - the parent should handle this gracefully
            onAddQueryToggle(query);
        }
    }, [getQueryForDataIndex, onAddQueryToggle]);

    // Track previous response content and job status to detect new responses
    const prevResponseContentRef = useRef<string | undefined>(undefined);
    const prevOutputStatusRef = useRef<string | undefined>(undefined);
    const prevJobStatusRef = useRef<string | undefined>(undefined);
    const prevJobIDRef = useRef<string | undefined>(undefined);
    const hasSeenRunningRef = useRef<boolean>(false);
    const hasAnimatedThisResponseRef = useRef<boolean>(false);
    const [shouldAnimateReveal, setShouldAnimateReveal] = useState(false);

    useEffect(() => {
        if (responseBoxRef.current && status === "RUNNING" && statusMessage) {
            responseBoxRef.current.scrollTo({
                top: responseBoxRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [statusMessage, status, responseBoxRef]);

    // Detect when a new response is generated for the first time ever
    useEffect(() => {
        const currentResponseContent = loadedOutput?.Response?.content;
        const currentOutputStatus = outputState?.status;
        const currentJobStatus = status;
        const currentJobID = jobID;

        // Reset tracking when jobID changes
        if (prevJobIDRef.current !== currentJobID) {
            prevResponseContentRef.current = undefined;
            prevOutputStatusRef.current = undefined;
            prevJobStatusRef.current = currentJobStatus;
            prevJobIDRef.current = currentJobID;
            hasSeenRunningRef.current = currentJobStatus === "RUNNING";
            hasAnimatedThisResponseRef.current = false;
            setShouldAnimateReveal(false);
            return;
        }

        // Track if we've seen the job in RUNNING status
        if (currentJobStatus === "RUNNING") {
            hasSeenRunningRef.current = true;
        }

        // Detect new response: response content appears for the first time
        // AND we've seen the job RUNNING (meaning it was just generated, not loaded from cache)
        const contentAppeared = !prevResponseContentRef.current && currentResponseContent;
        const hasSeenRunning = hasSeenRunningRef.current;
        const isCurrentlyRunning = currentJobStatus === "RUNNING";
        const wasRunning = prevJobStatusRef.current === "RUNNING";
        const isNowCompleted = currentJobStatus !== "RUNNING";
        const contentChanged = prevResponseContentRef.current !== currentResponseContent;

        // This is a new response if:
        // 1. (We've seen the job in RUNNING status OR it's currently RUNNING OR it just transitioned from RUNNING) - meaning it was just generated, not loaded from cache
        // 2. AND response content appeared for the first time (was undefined, now has content)
        // 3. AND we haven't already animated this response
        const isNewResponse = (hasSeenRunning || isCurrentlyRunning || (wasRunning && isNowCompleted)) && contentAppeared && !hasAnimatedThisResponseRef.current;

        if (isNewResponse) {
            // New response detected - start animation
            setShouldAnimateReveal(true);
            hasAnimatedThisResponseRef.current = true;
        } else if (contentChanged && currentResponseContent) {
            // Content changed to something completely different - reset animation tracking
            hasAnimatedThisResponseRef.current = false;
            setShouldAnimateReveal(false);
        } else if (!currentResponseContent) {
            // Content disappeared - stop animation
            setShouldAnimateReveal(false);
        }
        // Otherwise, if content is the same and we're already animating, keep animating
        // (the MarkdownViewer will handle completing the animation)

        // Update refs
        prevResponseContentRef.current = currentResponseContent;
        prevOutputStatusRef.current = currentOutputStatus;
        prevJobStatusRef.current = currentJobStatus;
        prevJobIDRef.current = currentJobID;
    }, [loadedOutput?.Response?.content, outputState?.status, status, jobID]);

    return (
        <div ref={responseBoxRef}>
            {status === "RUNNING" && statusMessage
                ? <MarkdownViewer markdown={statusMessage} greyText={true} resetKey={jobID} />
                : status === "RUNNING" && <div style={{opacity: 0.5, color: darkMode ? "#ffffff" : "#000000"}}>{`Processing request${ellipsis}`}</div>
            }
            {outputState.status === "loading" && <div style={{opacity: 0.5, color: darkMode ? "#ffffff" : "#000000"}}>Loading dashboard...</div>}
            {isOutputLoadFailed && <div style={{color: "red"}}>{outputLoadError}</div>}
            {loadedOutput && <>
                {loadedOutput.Error
                    ? <div style={{whiteSpace: "pre-wrap", color: darkMode ? "#ffffff" : "#000000"}}>{loadedOutput.Error.content}</div>
                    : <>
                        {/* Render Data1-Data24 outputs sequentially, one under the next */}
                        {(() => {
                            // Build a simple map from index -> Data output
                            const dataMap: Record<number, any> = {};
                            for (let i = 1; i <= 24; i++) {
                                const dataKey = `Data${i}` as keyof typeof loadedOutput;
                                if (loadedOutput[dataKey]) {
                                    dataMap[i] = loadedOutput[dataKey];
                                }
                            }

                            const dataOutputs: any[] = [];
                            for (let i = 1; i <= 24; i++) {
                                const data = dataMap[i];
                                if (data) {
                                    dataOutputs.push({ index: i, data });
                                }
                            }

                            if (dataOutputs.length === 0) {
                                return <div style={{ color: "red" }}>The job completed but no data was generated.</div>;
                            }

                            return dataOutputs.map(({ index, data }) => {
                                return (() => {
                            const pieChartData = extractPieChartData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (pieChartData) {
                                return (
                                    <div key={`pie-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <PieChart
                                            data={pieChartData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (pieChartData.isDonutChart) {
                                                    // Donut chart CSV
                                                    const csvHeader = "pie_category,pie_value,pie_color,pie_type,pie_title\n";
                                                    const csvRows = pieChartData.slices.map(slice =>
                                                        `${escapeCsvValue(slice.label)},${escapeCsvValue(slice.value)},${escapeCsvValue(slice.color)},donut,${escapeCsvValue(pieChartData.title)}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${pieChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular pie chart CSV
                                                    const csvHeader = "pie_category,pie_value,pie_color,pie_title\n";
                                                    const csvRows = pieChartData.slices.map(slice =>
                                                        `${escapeCsvValue(slice.label)},${escapeCsvValue(slice.value)},${escapeCsvValue(slice.color)},${escapeCsvValue(pieChartData.title)}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${pieChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const barChartData = extractBarChartData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (barChartData) {
                                return (
                                    <div key={`bar-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <BarChart
                                            data={barChartData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (barChartData.isStacked) {
                                                    // Stacked bar chart CSV
                                                    const csvHeader = "bar_category,bar_value,bar_series,bar_color,bar_title,bar_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    barChartData.segments.forEach(segment => {
                                                        const seriesData = barChartData.series.find(s => s.name === segment.series);
                                                        csvRows.push(
                                                            `${escapeCsvValue(segment.category)},${escapeCsvValue(segment.value)},${escapeCsvValue(segment.series)},${escapeCsvValue(seriesData?.color || "")},${escapeCsvValue(barChartData.title)},${escapeCsvValue(barChartData.yAxisLabel || "")}`
                                                        );
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${barChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular bar chart CSV
                                                    const csvHeader = "bar_category,bar_value,bar_color,bar_title,bar_y_axis_label\n";
                                                    const csvRows = barChartData.bars?.map(bar =>
                                                        `${escapeCsvValue(bar.label)},${escapeCsvValue(bar.value)},${escapeCsvValue(bar.color)},${escapeCsvValue(barChartData.title)},${escapeCsvValue(barChartData.yAxisLabel || "")}`
                                                    ).join("\n") || "";
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${barChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const scatterPlotData = extractScatterPlotData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (scatterPlotData) {
                                return (
                                    <div key={`scatter-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <ScatterPlot
                                            data={scatterPlotData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (scatterPlotData.isBubbleChart) {
                                                    // Bubble chart CSV
                                                    const csvHeader = "scatter_x,scatter_y,scatter_label,scatter_series,scatter_size,scatter_color,scatter_title,scatter_x_axis_label,scatter_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    scatterPlotData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(point.label)},${escapeCsvValue(series.name)},${escapeCsvValue(point.size !== undefined ? point.size : "")},${escapeCsvValue(series.color)},${escapeCsvValue(scatterPlotData.title)},${escapeCsvValue(scatterPlotData.xAxisLabel || "")},${escapeCsvValue(scatterPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${scatterPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular scatter plot CSV
                                                    const csvHeader = "scatter_x,scatter_y,scatter_label,scatter_series,scatter_color,scatter_title,scatter_x_axis_label,scatter_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    scatterPlotData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(point.label)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(scatterPlotData.title)},${escapeCsvValue(scatterPlotData.xAxisLabel || "")},${escapeCsvValue(scatterPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${scatterPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const lineGraphData = extractLineGraphData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (lineGraphData) {
                                return (
                                    <div key={`line-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <LineGraph
                                            data={lineGraphData}
                                            darkMode={darkMode}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (lineGraphData.isAreaChart) {
                                                    // Area chart CSV
                                                    const csvHeader = "line_x,line_y,line_series,line_color,line_type,line_title,line_x_axis_label,line_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    lineGraphData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},area,${escapeCsvValue(lineGraphData.title)},${escapeCsvValue(lineGraphData.xAxisLabel || "")},${escapeCsvValue(lineGraphData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${lineGraphData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular line graph CSV
                                                    const csvHeader = "line_x,line_y,line_series,line_color,line_title,line_x_axis_label,line_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    lineGraphData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(lineGraphData.title)},${escapeCsvValue(lineGraphData.xAxisLabel || "")},${escapeCsvValue(lineGraphData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${lineGraphData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const histogramData = extractHistogramData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (histogramData) {
                                return (
                                    <div key={`hist-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <Histogram
                                            data={histogramData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (histogramData.isGrouped && histogramData.series) {
                                                    // Grouped histogram CSV
                                                    const csvHeader = "hist_bin,hist_count,hist_frequency,hist_y_type,hist_series,hist_color,hist_title,hist_x_axis_label,hist_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    histogramData.series.forEach(series => {
                                                        series.bins.forEach(bin => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(bin.bin)},${escapeCsvValue(bin.count)},${escapeCsvValue(bin.frequency)},${escapeCsvValue(histogramData.yType)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(histogramData.title)},${escapeCsvValue(histogramData.xAxisLabel || "")},${escapeCsvValue(histogramData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${histogramData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else if (histogramData.bins) {
                                                    // Regular histogram CSV
                                                    const csvHeader = "hist_bin,hist_count,hist_frequency,hist_y_type,hist_color,hist_title,hist_x_axis_label,hist_y_axis_label\n";
                                                    const csvRows = histogramData.bins.map(bin =>
                                                        `${escapeCsvValue(bin.bin)},${escapeCsvValue(bin.count)},${escapeCsvValue(bin.frequency)},${escapeCsvValue(histogramData.yType)},${escapeCsvValue(bin.color)},${escapeCsvValue(histogramData.title)},${escapeCsvValue(histogramData.xAxisLabel || "")},${escapeCsvValue(histogramData.yAxisLabel || "")}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${histogramData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const boxPlotData = extractBoxPlotData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (boxPlotData) {
                                return (
                                    <div key={`box-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <BoxPlot
                                            data={boxPlotData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                if (boxPlotData.isGrouped && boxPlotData.series) {
                                                    // Grouped box plot CSV
                                                    const csvHeader = "box_category,box_value,box_series,box_color,box_title,box_x_axis_label,box_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    boxPlotData.series.forEach(series => {
                                                        series.boxes.forEach(box => {
                                                            box.values.forEach(value => {
                                                                csvRows.push(
                                                                    `${escapeCsvValue(box.category)},${escapeCsvValue(value)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(boxPlotData.title)},${escapeCsvValue(boxPlotData.xAxisLabel || "")},${escapeCsvValue(boxPlotData.yAxisLabel || "")}`
                                                                );
                                                            });
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${boxPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else if (boxPlotData.boxes) {
                                                    // Regular box plot CSV
                                                    const csvHeader = "box_category,box_value,box_color,box_title,box_x_axis_label,box_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    boxPlotData.boxes.forEach(box => {
                                                        box.values.forEach(value => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(box.category)},${escapeCsvValue(value)},${escapeCsvValue(box.color)},${escapeCsvValue(boxPlotData.title)},${escapeCsvValue(boxPlotData.xAxisLabel || "")},${escapeCsvValue(boxPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${boxPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const heatmapData = extractHeatmapData(
                                data.content.columns,
                                data.content.dataSource
                            );

                            if (heatmapData) {
                                return (
                                    <div key={`heatmap-${index}`} ref={index === 1 ? tableContainerRef : undefined} style={{ marginBottom: "1em" }}>
                                        <Heatmap
                                            data={heatmapData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={() => handleAddQuery(index)}
                                            onDownloadCsv={() => {
                                                // Heatmap CSV
                                                const csvHeader = "heatmap_x,heatmap_y,heatmap_value,heatmap_title,heatmap_x_axis_label,heatmap_y_axis_label\n";
                                                const csvRows = heatmapData.cells.map(cell =>
                                                    `${escapeCsvValue(cell.x)},${escapeCsvValue(cell.y)},${escapeCsvValue(cell.value)},${escapeCsvValue(heatmapData.title)},${escapeCsvValue(heatmapData.xAxisLabel || "")},${escapeCsvValue(heatmapData.yAxisLabel || "")}`
                                                ).join("\n");
                                                const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                const link = document.createElement('a');
                                                link.setAttribute('href', csvContent);
                                                const filename = `${heatmapData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                link.setAttribute('download', filename);
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                        />
                                    </div>
                                );
                            }

                            return (
                                <DataTableView
                                    key={`data-${index}`}
                                    columns={data.content.columns}
                                    dataSource={data.content.dataSource}
                                    darkMode={darkMode}
                                    parentJobAlias={parentJobAlias}
                                    defaultPageSize={defaultPageSize}
                                    tableContainerRef={tableContainerRef}
                                    onAddQuery={() => handleAddQuery(index)}
                                />
                            );
                                })();
                            });
                        })()}
                    </>
                }
            </>}
        </div>
    );
};

